import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@design-system';
import {
  EDJ_QUICK_PLAN_MARITAL_CATEGORIES,
  EDJ_QUICK_PLAN_SEX_OPTIONS,
  QUICK_EDIT_DISABILITY_OPTIONS,
  QUICK_EDIT_GENDER_OPTIONS,
  QUICK_EDIT_RACE_OPTIONS,
} from '@shared';
import clientIntakeFormSchema from '@shared/utils/clientIntakeFormSchema.json';
import { onlyDigitsKeyDown, QUICK_EDIT_HOUSEHOLD_FIELDS } from './quick-edit-field-validation';

const schema = clientIntakeFormSchema as Record<string, { type: string; label: string }>;

const HEIGHT_OPTIONS = Array.from({ length: 37 }, (_, index) => {
  const inches = index + 48;
  return `${Math.floor(inches / 12)}'${inches % 12}"`;
});

const SELECT_FIELDS: Record<string, readonly string[]> = {
  clientGenderAtBirth: QUICK_EDIT_GENDER_OPTIONS,
  clientRace: QUICK_EDIT_RACE_OPTIONS,
  clientHeightInches: HEIGHT_OPTIONS,
  clientDisabilityWorkersComp: QUICK_EDIT_DISABILITY_OPTIONS,
};

const EDJ_SELECT_FIELDS: Record<string, readonly string[]> = {
  clientGenderAtBirth: EDJ_QUICK_PLAN_SEX_OPTIONS,
  clientMaritalStatus: EDJ_QUICK_PLAN_MARITAL_CATEGORIES,
};

export function formatQuickEditFieldValue(fieldName: string, value: unknown): string {
  if (value == null) return '';

  if (fieldName === 'clientHeightInches') {
    const text = String(value);
    if (text.includes("'")) return text;
    const inches = Number(value);
    return Number.isFinite(inches) && inches > 0
      ? `${Math.floor(inches / 12)}'${inches % 12}"`
      : text;
  }

  if (schema[fieldName]?.type === 'date') {
    const date = new Date(value as string | number | Date);
    return Number.isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
  }

  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

export function parseQuickEditFieldValue(fieldName: string, value: string): string | number | null {
  if (value === '') return null;

  if (fieldName === 'clientHeightInches') {
    const match = value.match(/(\d+)'[\s]*(\d+)/);
    return match ? parseInt(match[1], 10) * 12 + parseInt(match[2], 10) : null;
  }

  return value;
}

export function QuickEditFieldInput({
  fieldName,
  id,
  value,
  onValueChange,
  onBlur,
  edjQuickPlan = false,
  className,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: {
  'fieldName': string;
  'id': string;
  'value': string;
  'onValueChange': (value: string) => void;
  'onBlur'?: () => void;
  'edjQuickPlan'?: boolean;
  'className'?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}) {
  const options = (edjQuickPlan && EDJ_SELECT_FIELDS[fieldName]) || SELECT_FIELDS[fieldName];

  if (options) {
    return (
      <Select
        value={value}
        onValueChange={onValueChange}
      >
        <SelectTrigger
          id={id}
          className={className ?? 'w-full min-w-0'}
          onBlur={onBlur}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
        >
          <SelectValue
            placeholder={`Select ${schema[fieldName]?.label.toLowerCase() ?? 'an answer'}`}
          />
        </SelectTrigger>
        <SelectContent>
          {options.map(option => (
            <SelectItem
              key={option}
              value={option}
            >
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  const type =
    schema[fieldName]?.type === 'date'
      ? 'date'
      : schema[fieldName]?.type === 'integer' || schema[fieldName]?.type === 'number'
        ? 'number'
        : 'text';
  const isZipCode = fieldName === 'clientZipCode' || fieldName === 'clientLtcZipCode';
  // Native number inputs step on wheel events, silently changing amounts while scrolling.
  const isAssetAmount = fieldName === 'householdTotalAssetsValue';

  return (
    <Input
      id={id}
      className={className}
      type={isZipCode || isAssetAmount ? 'text' : type}
      inputMode={isZipCode ? 'numeric' : isAssetAmount ? 'decimal' : undefined}
      value={value}
      onChange={event => onValueChange(event.target.value)}
      onBlur={onBlur}
      aria-invalid={ariaInvalid}
      aria-describedby={ariaDescribedBy}
      {...(isZipCode
        ? {
            maxLength: 5,
            minLength: 5,
            onKeyDown: onlyDigitsKeyDown,
          }
        : {})}
      {...(QUICK_EDIT_HOUSEHOLD_FIELDS.has(fieldName) ? { min: 0, max: 50 } : {})}
    />
  );
}
